import { gql, useMutation } from "@apollo/client";
import { useCallback, useContext, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { login } from "../apollo/server";
import UserContext from "../context/User";
import Analytics from "../utils/analytics";

const LOGIN = gql`
  ${login}
`;

function useRegistration() {
  const { setTokenAsync } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const emailRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [login, setLogin] = useState(false);
  const [loginButton, loginButtonSetter] = useState(null);
  const [Login] = useMutation(LOGIN, { onCompleted, onError });

  const toggleSnackbar = useCallback(() => {
    setLoginError("");
    setLogin(false);
  }, []);

  async function onCompleted({ login }) {
    try {
      if (login.inNewUser) {
        await Analytics.identify(
          {
            userId: login.userId,
            name: login.name,
            email: login.email,
          },
          login.userId
        );
        await Analytics.track(Analytics.events.USER_CREATED_ACCOUNT, {
          userId: login.userId,
          name: login.name,
          email: login.email,
        });
      } else {
        await Analytics.identify(
          {
            userId: login.userId,
            name: login.name,
            email: login.email,
          },
          login.userId
        );
        await Analytics.track(Analytics.events.USER_LOGGED_IN, {
          userId: login.userId,
          name: login.name,
          email: login.email,
        });
      }
      setLogin(true);
      await setTokenAsync(login.token);
    } catch (e) {
      setLoginError("Something went wrong");
      console.log("Error While saving token:", e);
    } finally {
      setLoading(false);
    }
  }

  function onError(errors) {
    setLoading(false);
    // An account they deleted: offer to bring it back (a code to their email).
    if (/account is deactivated/i.test(errors.message || "")) {
      navigate("/reactivate", {
        state: { email: emailRef.current, from: location.state?.from },
      });
      return;
    }
    setLoginError(errors.message || "Invalid credentials!");
  }

  const authenticationFailure = useCallback((response) => {
    console.log("Authentication Failed: ", response);
    setLoading(false);
    loginButtonSetter(null);
    setLoginError("Something went wrong");
  }, []);

  const mutateLogin = useCallback(
    async (user) => {
      emailRef.current = user.email;
      Login({
        variables: {
          ...user,
        },
      });
    },
    [Login]
  );

  const goolgeSuccess = useCallback(
    (credentialResponse) => {
      // @react-oauth/google returns a JWT credential — decode payload
      try {
        const base64 = credentialResponse.credential.split('.')[1];
        const decoded = JSON.parse(atob(base64.replace(/-/g, '+').replace(/_/g, '/')));
        const user = {
          phone: "",
          email: decoded.email,
          password: "",
          name: decoded.name,
          picture: decoded.picture,
          type: "google",
        };
        mutateLogin(user);
      } catch (e) {
        console.error("Failed to decode Google credential:", e);
      }
    },
    [mutateLogin]
  );

  return {
    loading,
    setLoading,
    loginButton,
    loginButtonSetter,
    mutateLogin,
    goolgeSuccess,
    authenticationFailure,
    setLoginError,
    loginError,
    login,
    toggleSnackbar,
  };
}

export default useRegistration;
